/**
 * CON-164. This used to be `resolveConfig('../css/tailwind.config')` — an entry point
 * Tailwind v4 removes, called with a STRING rather than a config object, against a path
 * (`src/css/tailwind.config`) that does not exist. resolveConfig ignored the argument, so
 * `tailwindConfig()` has always returned Tailwind's **stock v3 default theme**, never this
 * app's. The dashboard's own colours, spacing and type scale never reached a chart.
 *
 * The 37 call sites across the chart files are therefore preserved exactly by freezing the
 * stock values they actually read. Each was copied from the live `resolveConfig` output on
 * 2026-09-16 before the migration, so every chart renders the same pixels it did.
 *
 * This is deliberately NOT wired to `--cs-*` or to the dashboard's theme. Pointing charts at
 * real design values is a visible change and belongs to an issue that intends one; decision
 * D1 keeps the dashboard's identity out of the design system's scope, and this migration is a
 * build change, not a redesign.
 *
 * Adding a new chart value means adding it here with its stock-v3 value, or making a
 * deliberate, reviewed decision to use a different one.
 */
const CHART_THEME = Object.freeze({
  theme: Object.freeze({
    colors: Object.freeze({
      white: '#fff',
      red: Object.freeze({ 500: '#ef4444' }),
      gray: Object.freeze({
        100: '#f3f4f6',
        200: '#e5e7eb',
        400: '#9ca3af',
        500: '#6b7280',
        800: '#1f2937',
      }),
    }),
    margin: Object.freeze({ 2: '0.5rem', 3: '0.75rem', 4: '1rem' }),
    width: Object.freeze({ 3: '0.75rem' }),
    height: Object.freeze({ 3: '0.75rem' }),
    borderRadius: Object.freeze({ full: '9999px' }),
    fontWeight: Object.freeze({ bold: '700' }),
    fontSize: Object.freeze({
      sm: Object.freeze(['0.875rem', Object.freeze({ lineHeight: '1.25rem' })]),
      '3xl': Object.freeze(['1.875rem', Object.freeze({ lineHeight: '2.25rem' })]),
    }),
  }),
});

export const tailwindConfig = () => CHART_THEME;

export const hexToRGB = (h) => {
  let r = 0;
  let g = 0;
  let b = 0;
  if (h.length === 4) {
    r = `0x${h[1]}${h[1]}`;
    g = `0x${h[2]}${h[2]}`;
    b = `0x${h[3]}${h[3]}`;
  } else if (h.length === 7) {
    r = `0x${h[1]}${h[2]}`;
    g = `0x${h[3]}${h[4]}`;
    b = `0x${h[5]}${h[6]}`;
  }
  return `${+r},${+g},${+b}`;
};

export const formatValue = (value) => Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumSignificantDigits: 3,
  notation: 'compact',
}).format(value);
